/* eslint-disable */
import React, {useEffect, useRef, useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import PompiLogo from '../../assets/PompiImage.svg';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import AuthHeader from '../../components/AuthHeader';
import AuthInput from '../../components/AuthInput';
import AuthButton from '../../components/AuthButton';
import {colors, fonts} from '../../config/theme';
import {isValidEmail} from '../../utility/validation';
import {PASSWORD_RESET_ENABLED} from '../../config/features';
import Toast from 'react-native-toast-message';
import {useDispatch, useSelector} from 'react-redux';
import {loginUser} from '../../slice/loginSlice';
import {useTranslation} from 'react-i18next';

const LoginEmail = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const insets = useSafeAreaInsets();
  const { isLoading } = useSelector(state => state.login);
  const [email, setEmail] = useState(route.params?.email ?? '');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const passwordRef = useRef(null);

  // UpdatePassword comes back here with the email after a reset: fill it in
  // and clear the old password.
  const prefillEmail = route.params?.email;
  useEffect(() => {
    if (prefillEmail) {
      setEmail(prefillEmail);
      setPassword('');
      setErrors({});
    }
  }, [prefillEmail]);

  const validate = () => {
    const next = {};
    if (!email.trim()) {
      next.email = t(`root.Auth.emailRequired`);
    } else if (!isValidEmail(email)) {
      next.email = t(`root.Auth.emailInvalid`);
    }
    if (!password) {
      next.password = t(`root.Auth.passwordRequired`);
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleLogin = async () => {
    if (!validate()) {
      return;
    }

    try {
      await dispatch(loginUser({ email: email.trim(), password })).unwrap();
      Toast.show({
        type: 'success',
        text1: t(`root.Toast.Success.LoginSuccess`),
      });
    } catch (err) {
      Toast.show({
        type: 'error',
        text1: err || t(`root.Toast.Error.InvalidCredentials`),
      });
    }
  };

  // Clear a field's error as soon as the user starts fixing it.
  const onChange = (field, setter) => value => {
    setter(value);
    if (errors[field]) {
      setErrors(prev => ({...prev, [field]: undefined}));
    }
  };

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t(`root.LoginEmail.title`)}
        onBack={() => navigation.goBack()}
      />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            {paddingBottom: insets.bottom + 24},
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.logoWrapper}>
            <PompiLogo width={140} height={134} />
          </View>

          <View style={styles.form}>
            <AuthInput
              label={t(`root.LoginEmail.email`)}
              placeholder={t(`root.LoginEmail.emailPlaceholder`)}
              value={email}
              onChangeText={onChange('email', setEmail)}
              error={errors.email}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              // "username" pairs this field with the password for Keychain
              // AutoFill, even though the account id is an email.
              textContentType="username"
              returnKeyType="next"
              submitBehavior="submit"
              onSubmitEditing={() => passwordRef.current?.focus()}
            />
            <AuthInput
              ref={passwordRef}
              secure
              label={t(`root.LoginEmail.password`)}
              placeholder={t(`root.LoginEmail.passwordPlaceholder`)}
              value={password}
              onChangeText={onChange('password', setPassword)}
              error={errors.password}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="current-password"
              textContentType="password"
              returnKeyType="go"
              onSubmitEditing={handleLogin}
            />
            {PASSWORD_RESET_ENABLED ? (
              <Pressable
                onPress={() =>
                  navigation.navigate('ForgotPassword', {email: email.trim()})
                }
                hitSlop={8}
                accessibilityRole="button"
                style={({ pressed }) => [
                  styles.forgotPassword,
                  pressed && styles.linkPressed,
                ]}>
                <Text style={styles.forgotPasswordText}>
                  {t(`root.LoginEmail.ForgotPassword`)}
                </Text>
              </Pressable>
            ) : null}

            <AuthButton
              variant="primary"
              title={t(`root.LoginEmail.Login`)}
              iconRight={
                <ArrowRight width={16} height={16} color={colors.onPrimary} />
              }
              loading={isLoading}
              onPress={handleLogin}
              style={styles.loginButton}
            />

            <Pressable
              onPress={() => navigation.navigate('CreateAccount')}
              hitSlop={8}
              accessibilityRole="link"
              style={({ pressed }) => [
                styles.signUpRow,
                pressed && styles.linkPressed,
              ]}>
              <Text style={styles.signUpText}>
                {t(`root.LoginEmail.NoAccount`)}{' '}
                <Text style={styles.signUpLink}>{t(`root.LoginEmail.SignUp`)}</Text>
              </Text>
            </Pressable>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.surface,
  },
  flex: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  logoWrapper: {
    alignItems: 'center',
    paddingTop: 16,
    paddingBottom: 8,
  },
  form: {
    paddingHorizontal: 24,
    gap: 16,
  },
  forgotPassword: {
    alignSelf: 'flex-end',
    marginTop: -4,
  },
  forgotPasswordText: {
    fontFamily: fonts.semiBold,
    fontSize: 13,
    lineHeight: 18,
    color: colors.primary,
  },
  loginButton: {
    marginTop: 8,
  },
  signUpRow: {
    alignSelf: 'center',
    marginTop: 8,
  },
  linkPressed: {
    opacity: 0.6,
  },
  signUpText: {
    fontFamily: fonts.regular,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textMuted,
  },
  signUpLink: {
    fontFamily: fonts.bold,
    color: colors.primary,
  },
});

export default LoginEmail;
